import { useState } from 'react'
import { motion } from 'framer-motion'
import { Tabs, TabList, Tab, TabPanels, TabPanel } from '../components/ui/Tabs'
import {
  Users, Shield, Building, CreditCard, Smartphone, Database, Settings, Bell,
  Key, Globe, Palette, Moon, Sun, Monitor, ChevronDown, ChevronUp,
  Plus, Edit, Trash2, Eye, Download, Upload, Save, Loader2,
  ToggleLeft, ToggleRight, Mail, Lock, AlertTriangle, CheckCircle, XCircle,
  MoreVertical, Truck
} from 'lucide-react'
import { cn, formatCurrency } from '../lib/utils'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { productsApi, queryKeys } from '../lib/api'
import { Button } from '../components/ui/Button'
import { Input, Select, Textarea } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { Dropdown } from '../components/ui/Dropdown'
import { Switch } from '../components/ui/Switch'
import { useUIStore } from '../stores/uiStore'
import { useAuthStore, usePermissions } from '../stores/authStore'
import toast from 'react-hot-toast'

const settingsTabs = [
  { id: 'company', label: 'شرکت', icon: Building },
  { id: 'users', label: 'کاربران', icon: Users },
  { id: 'roles', label: 'نقش‌ها', icon: Shield },
  { id: 'plans', label: 'پلن‌ها', icon: CreditCard },
  { id: 'devices', label: 'دستگاه‌ها', icon: Smartphone },
  { id: 'notifications', label: 'اعلان‌ها', icon: Bell },
  { id: 'appearance', label: 'ظاهر', icon: Palette },
  { id: 'integrations', label: 'یکپارچه‌سازی', icon: Globe },
  { id: 'advanced', label: 'پیشرفته', icon: Settings },
]

export default function SettingsPage() {
  const { modals, openModal, closeModal } = useUIStore()
  const { hasPermission } = usePermissions()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState('company')

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">تنظیمات</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">مدیریت تنظیمات سیستم و کاربران</p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card overflow-hidden"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="p-2 bg-neutral-100/80 dark:bg-neutral-800/80 border-b border-neutral-200 dark:border-neutral-800 rounded-t-xl overflow-x-auto">
            <TabList className="bg-transparent p-0 gap-1.5 flex-nowrap min-w-max">
              {settingsTabs.map((tab) => (
                <Tab key={tab.id} value={tab.id} className="flex items-center gap-2 px-3.5 py-2 whitespace-nowrap shrink-0">
                  <tab.icon className="w-4 h-4 shrink-0" />
                  <span>{tab.label}</span>
                </Tab>
              ))}
            </TabList>
          </div>

          <TabPanels className="p-6">
            {/* Company Tab */}
            <TabPanel value="company" className="space-y-6 max-w-3xl">
              <h3 className="font-semibold text-neutral-900 dark:text-white">اطلاعات شرکت</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="نام شرکت" placeholder="نام شرکت شما" />
                <Input label="نام تجاری" placeholder="نام تجاری (اختیاری)" />
                <Input label="شماره ثبت" placeholder="شماره ثبت شرکت" />
                <Input label="شناسه ملی" placeholder="شناسه ملی" />
                <Input label="شماره تلفن" type="tel" placeholder="۰۲۱-۱۲۳۴۵۶۷۸" />
                <Input label="ایمیل" type="email" placeholder="info@company.com" />
                <Textarea label="آدرس" placeholder="آدرس کامل شرکت" rows={3} className="md:col-span-2" />
              </div>

              <h3 className="font-semibold text-neutral-900 dark:text-white pt-6 border-t border-neutral-200 dark:border-neutral-800">برندینگ</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col items-center p-4 rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-700">
                  <div className="w-24 h-24 rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-2">
                    <Building className="w-10 h-10 text-neutral-400" />
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">لوگو</p>
                  <Button variant="outline" size="sm" className="mt-2">آپلود</Button>
                </div>
                <div className="flex flex-col items-center p-4 rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-700">
                  <div className="w-24 h-12 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mb-2">
                    <span className="text-primary-600 dark:text-primary-400 font-bold">Primary</span>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">رنگ اصلی</p>
                  <Input type="color" className="w-12 h-12 rounded-lg border-0 p-0 mt-2" defaultValue="#2563eb" />
                </div>
                <div className="flex flex-col items-center p-4 rounded-xl border-2 border-dashed border-neutral-300 dark:border-neutral-700">
                  <div className="w-24 h-12 rounded-lg bg-neutral-900 dark:bg-neutral-100 flex items-center justify-center mb-2">
                    <span className="text-white dark:text-black font-bold">Dark</span>
                  </div>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">رنگ ثانویه</p>
                  <Input type="color" className="w-12 h-12 rounded-lg border-0 p-0 mt-2" defaultValue="#1f2937" />
                </div>
              </div>
            </TabPanel>

            {/* Users Tab */}
            <TabPanel value="users" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">مدیریت کاربران</h3>
                <Button onClick={() => openModal('create-user')} disabled={!hasPermission('user', 'create')}>
                  <Plus className="w-4 h-4 ml-1" />
                  کاربر جدید
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>نام</th>
                      <th>ایمیل</th>
                      <th className="hidden sm:table-cell">نقش</th>
                      <th className="hidden md:table-cell">وضعیت</th>
                      <th className="hidden lg:table-cell">آخرین ورود</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'احمد محمدی', email: 'ahmad@company.com', role: 'مدیر سیستم', status: 'active', last_login: '۲ ساعت پیش' },
                      { name: 'فاطمه رضایی', email: 'fateme@company.com', role: 'مدیر انبار', status: 'active', last_login: '۱ روز پیش' },
                      { name: 'علی کریمی', email: 'ali@company.com', role: 'فروشنده', status: 'active', last_login: '۳ ساعت پیش' },
                    ].map((user, i) => (
                      <tr key={i}>
                        <td className="font-medium text-neutral-900 dark:text-white">{user.name}</td>
                        <td className="text-neutral-600 dark:text-neutral-400">{user.email}</td>
                        <td className="hidden sm:table-cell"><Badge variant="primary" className="text-xs">{user.role}</Badge></td>
                        <td className="hidden md:table-cell"><Badge variant={user.status === 'active' ? 'success' : 'neutral'} className="text-xs">{user.status === 'active' ? 'فعال' : 'غیرفعال'}</Badge></td>
                        <td className="hidden lg:table-cell text-neutral-500 dark:text-neutral-400 text-sm">{user.last_login}</td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={[
                              { label: 'مشاهده', icon: Eye, onClick: () => {} },
                              { label: 'ویرایش', icon: Edit, onClick: () => {}, disabled: !hasPermission('user', 'update') },
                              { type: 'divider' },
                              { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => {}, disabled: !hasPermission('user', 'delete') },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabPanel>

            {/* Roles Tab */}
            <TabPanel value="roles" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">نقش‌ها و مجوزها</h3>
                <Button onClick={() => openModal('create-role')} disabled={!hasPermission('role', 'create')}>
                  <Plus className="w-4 h-4 ml-1" />
                  نقش جدید
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { name: 'مدیر سیستم', permissions: 45, users: 2, description: 'دسترسی کامل به تمام بخش‌ها' },
                  { name: 'مدیر انبار', permissions: 18, users: 3, description: 'مدیریت موجودی، دریافت، انتقال' },
                  { name: 'فروشنده', permissions: 12, users: 5, description: 'نقدی، فروش، مشتریان' },
                  { name: 'حسابدار', permissions: 15, users: 1, description: 'گزارشات مالی، بیل‌ها، تسویه' },
                  { name: 'مشاهده‌کننده', permissions: 8, users: 4, description: 'فقط مشاهده گزارش‌ها' },
                ].map((role, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="card p-5 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-semibold text-neutral-900 dark:text-white">{role.name}</h4>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{role.description}</p>
                      </div>
                      <Badge variant="primary" className="text-xs">{role.permissions} مجوز</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
                      <span>{role.users} کاربر</span>
                      <Dropdown
                        trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                        items={[
                          { label: 'ویرایش مجوزها', icon: Edit, onClick: () => {} },
                          { label: 'مشاهده کاربران', icon: Users, onClick: () => {} },
                          { type: 'divider' },
                          { label: 'حذف نقش', icon: Trash2, className: 'text-danger-600', onClick: () => {} },
                        ]}
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </TabPanel>

            {/* Plans Tab */}
            <TabPanel value="plans" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">پلن‌های اشتراک</h3>
                <Button onClick={() => openModal('create-plan')} disabled={!hasPermission('plan', 'create')}>
                  <Plus className="w-4 h-4 ml-1" />
                  پلن جدید
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { name: 'رایگان', price: 0, period: 'ماهانه', features: ['۵ کاربر', '۱۰۰ کالا', '۱ انبار', 'پشتیبانی ایمیل'], popular: false },
                  { name: 'حرفه‌ای', price: 299000, period: 'ماهانه', features: ['۲۵ کاربر', '۱۰۰۰ کالا', '۵ انبار', 'پشتیبانی تلفنی', 'API'], popular: true },
                  { name: 'سازمانی', price: 799000, period: 'ماهانه', features: ['نامحدود کاربر', 'نامحدود کالا', 'نامحدود انبار', 'پشتیبانی ۲۴/۷', 'API', 'SSO', 'محل'], popular: false },
                ].map((plan, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className={cn('card p-6 relative', plan.popular && 'border-2 border-primary-500 shadow-lg')}>
                    {plan.popular && <div className="absolute -top-3 right-4"><Badge variant="primary" className="text-xs">محبوب</Badge></div>}
                    <h4 className="font-semibold text-neutral-900 dark:text-white">{plan.name}</h4>
                    <div className="my-4">
                      <span className="text-3xl font-bold text-neutral-900 dark:text-white">{formatCurrency(plan.price)}</span>
                      <span className="text-neutral-500 dark:text-neutral-400 ml-2">/{plan.period}</span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      {plan.features.map((f, j) => (
                        <li key={j} className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                          <CheckCircle className="w-4 h-4 text-success-500 flex-shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Button variant={plan.popular ? 'primary' : 'outline'} className="w-full">
                      {plan.popular ? 'جاری است' : 'انتخاب'}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </TabPanel>

            {/* Devices Tab */}
            <TabPanel value="devices" className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-neutral-900 dark:text-white">مدیریت دستگاه‌ها</h3>
                <Button onClick={() => openModal('register-device')}>
                  <Plus className="w-4 h-4 ml-1" />
                  ثبت دستگاه
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>نام دستگاه</th>
                      <th className="hidden sm:table-cell">نوع</th>
                      <th className="hidden md:table-cell">شناسه</th>
                      <th className="hidden lg:table-cell">آخرین فعال‌سازی</th>
                      <th>وضعیت</th>
                      <th className="w-16"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'پاس مرکز اصلی', type: 'POS Terminal', id: 'POS-001', last: '۱۰ دقیقه پیش', status: 'active' },
                      { name: 'اسکنر انبار شمالی', type: 'Barcode Scanner', id: 'SCN-003', last: '۲ ساعت پیش', status: 'active' },
                      { name: 'پرینتر رسید', type: 'Receipt Printer', id: 'PRT-001', last: '۱ روز پیش', status: 'inactive' },
                    ].map((d, i) => (
                      <tr key={i}>
                        <td className="font-medium text-neutral-900 dark:text-white">{d.name}</td>
                        <td className="hidden sm:table-cell"><Badge variant="neutral" className="text-xs">{d.type}</Badge></td>
                        <td className="hidden md:table-cell font-mono text-sm text-neutral-600 dark:text-neutral-400">{d.id}</td>
                        <td className="hidden lg:table-cell text-neutral-500 dark:text-neutral-400 text-sm">{d.last}</td>
                        <td><Badge variant={d.status === 'active' ? 'success' : 'neutral'} className="text-xs">{d.status === 'active' ? 'آنلاین' : 'آفلاین'}</Badge></td>
                        <td>
                          <Dropdown
                            trigger={<button className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"><MoreVertical className="w-4 h-4" /></button>}
                            items={[
                              { label: 'مشاهده', icon: Eye, onClick: () => {} },
                              { label: 'لغو авториز', icon: Lock, onClick: () => {}, className: 'text-warning-600' },
                              { type: 'divider' },
                              { label: 'حذف', icon: Trash2, className: 'text-danger-600', onClick: () => {} },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabPanel>

            {/* Notifications Tab */}
            <TabPanel value="notifications" className="space-y-6 max-w-2xl">
              <h3 className="font-semibold text-neutral-900 dark:text-white">تنظیمات اعلان‌ها</h3>
              <div className="space-y-4">
                {[
                  { title: 'اعلان‌های ایمیل', desc: 'دریافت اعلان‌های مهم از طریق ایمیل', enabled: true },
                  { title: 'اعلان‌های سیستم', desc: 'نمایش اعلان‌ها در پنل مدیریت', enabled: true },
                  { title: 'اعلان‌های موجودی کم', desc: 'هشدار وقتی کالا زیر حداقل رسید', enabled: true },
                  { title: 'اعلان‌های سفارش جدید', desc: 'اطلاع از سفارش‌های خرید جدید', enabled: false },
                  { title: 'اعلان‌های پرداخت', desc: 'تایید پرداخت‌های موفق', enabled: true },
                  { title: 'اعلان‌های شیفت', desc: 'یادآوری باز/بستن شیفت', enabled: false },
                ].map((notif, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
                    <div>
                      <p className="font-medium text-neutral-900 dark:text-white">{notif.title}</p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">{notif.desc}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked={notif.enabled} className="sr-only peer" />
                      <div className="w-11 h-6 bg-neutral-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-neutral-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-neutral-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </TabPanel>

            {/* Appearance Tab */}
            <TabPanel value="appearance" className="space-y-6 max-w-2xl">
              <h3 className="font-semibold text-neutral-900 dark:text-white">تنظیمات ظاهر</h3>

              <div className="card p-5">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-4">حالت تم</h4>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { value: 'light', label: 'روشن', icon: Sun, desc: ' همیشه حالت روز' },
                    { value: 'dark', label: 'تیره', icon: Moon, desc: ' همیشه حالت شب' },
                    { value: 'system', label: 'سیستم', icon: Monitor, desc: ' طبق تنظیمات دستگاه' },
                  ].map((theme) => (
                    <button key={theme.value} className={cn(
                      'p-4 rounded-xl border-2 text-right transition-all',
                      'border-neutral-300 dark:border-neutral-700 hover:border-primary-400'
                    )}>
                      <div className="flex items-center gap-3">
                        <theme.icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">{theme.label}</p>
                          <p className="text-xs text-neutral-500 dark:text-neutral-400">{theme.desc}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="card p-5">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-4">رترنگ رنگ</h4>
                <div className="flex flex-wrap gap-3">
                  {['#2563eb', '#059669', '#dc2626', '#7c3aed', '#ea580c', '#0891b2', '#65a30d', '#be185d'].map((color) => (
                    <button key={color} className={cn(
                      'w-10 h-10 rounded-xl border-2 transition-transform hover:scale-110',
                      `bg-[${color}]`
                    )} style={{ boxShadow: `0 0 0 2px ${color}40` }} />
                  ))}
                </div>
              </div>

              <div className="card p-5">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-4">فونت</h4>
                <Select
                  options={[
                    { value: 'vazirmatn', label: 'وزیر متن (پیش‌فرض)' },
                    { value: 'iransans', label: 'ایران سنس' },
                    { value: 'yekan', label: 'یکان' },
                    { value: 'system', label: 'سیستم پیش‌فرض' },
                  ]}
                  placeholder="انتخاب فونت"
                />
              </div>
            </TabPanel>

            {/* Integrations Tab */}
            <TabPanel value="integrations" className="space-y-6 max-w-2xl">
              <h3 className="font-semibold text-neutral-900 dark:text-white">یکپارچه‌سازی‌ها</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: 'زرین‌پال', desc: 'درگاه پرداخت آنلاین', status: 'connected', icon: CreditCard },
                  { name: 'ایدی‌پی', desc: 'درگاه پرداخت جایگزین', status: 'disconnected', icon: CreditCard },
                  { name: 'سازمان ملی پست', desc: 'پیگیری مرسوله‌ها', status: 'connected', icon: Truck },
                  { name: 'تلگرام', desc: 'ربات اعلان‌ها', status: 'connected', icon: Mail },
                  { name: 'اس‌ام‌اس‌.ای‌ار', desc: 'ارسال پیامک', status: 'disconnected', icon: Mail },
                  { name: 'گوگل درایو', desc: 'پشتیبان‌گیری فایل‌ها', status: 'disconnected', icon: Globe },
                ].map((int, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="card p-5">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                          <int.icon className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                          <p className="font-medium text-neutral-900 dark:text-white">{int.name}</p>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">{int.desc}</p>
                        </div>
                      </div>
                      <Badge variant={int.status === 'connected' ? 'success' : 'neutral'} className="text-xs">
                        {int.status === 'connected' ? 'متصل' : 'قطع شده'}
                      </Badge>
                    </div>
                    <Button variant={int.status === 'connected' ? 'outline' : 'primary'} size="sm" className="w-full">
                      {int.status === 'connected' ? 'تنظیمات' : 'اتصال'}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </TabPanel>

            {/* Advanced Tab */}
            <TabPanel value="advanced" className="space-y-6 max-w-2xl">
              <h3 className="font-semibold text-neutral-900 dark:text-white">تنظیمات پیشرفته</h3>

              <div className="card p-5">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-4">بازیابی اطلاعات</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="w-full"><Download className="w-4 h-4 ml-1" /> خروجی کامل دیتابیس</Button>
                  <Button variant="outline" className="w-full"><Upload className="w-4 h-4 ml-1" /> بازیابی از فایل پشتیبان</Button>
                  <Button variant="danger" className="w-full"><Trash2 className="w-4 h-4 ml-1" /> پاک کردن تمام داده‌ها</Button>
                  <Button variant="outline" className="w-full"><Database className="w-4 h-4 ml-1" /> بهینه‌سازی دیتابیس</Button>
                </div>
              </div>

              <div className="card p-5">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-4">لاگ‌های سیستم</h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center"><Database className="w-4 h-4 text-primary-600" /></div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">لاگ‌های دیتابیس</p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">۵ روز نگهداری • ۲.۳ مگابایت</p>
                      </div>
                    </div>
                    <Badge variant="success" className="text-xs">فعال</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-warning-100 dark:bg-warning-900/30 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-warning-600" /></div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">لاگ‌های خطا</p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">۳۰ روز نگهداری • ۴۵ کیلوبایت</p>
                      </div>
                    </div>
                    <Badge variant="success" className="text-xs">فعال</Badge>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-success-100 dark:bg-success-900/30 flex items-center justify-center"><CheckCircle className="w-4 h-4 text-success-600" /></div>
                      <div>
                        <p className="font-medium text-neutral-900 dark:text-white">لاگ‌های امنیتی</p>
                        <p className="text-sm text-neutral-500 dark:text-neutral-400">۹۰ روز نگهداری • ۱.۲ مگابایت</p>
                      </div>
                    </div>
                    <Badge variant="success" className="text-xs">فعال</Badge>
                  </div>
                </div>
              </div>

              <div className="card p-5 border-danger-200 dark:border-danger-800">
                <h4 className="font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-danger-600" />
                  منطقه خطرناک
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="danger" className="w-full"><Trash2 className="w-4 h-4 ml-1" /> ریست کامل سیستم</Button>
                  <Button variant="danger" className="w-full"><Database className="w-4 h-4 ml-1" /> Drop Database</Button>
                  <Button variant="outline" className="w-full"><Key className="w-4 h-4 ml-1" />Rotate API Keys</Button>
                  <Button variant="outline" className="w-full"><Lock className="w-4 h-4 ml-1" />Revoke All Sessions</Button>
                </div>
              </div>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </motion.div>

      {/* Modals */}
      <Modal isOpen={modals['create-user']} onClose={() => closeModal('create-user')} title="ایجاد کاربر جدید" size="md">
        <UserForm onSubmit={() => { closeModal('create-user'); toast.success('کاربر ایجاد شد') }} />
      </Modal>
      <Modal isOpen={modals['create-role']} onClose={() => closeModal('create-role')} title="ایجاد نقش جدید" size="md">
        <div className="space-y-4 p-2"><Input label="نام نقش" placeholder="مثال: مدیر فروش" /><Textarea label="توضیحات" rows={3} /><div className="flex justify-end gap-2"><Button variant="outline">انصراف</Button><Button>ایجاد</Button></div></div>
      </Modal>
      <Modal isOpen={modals['create-plan']} onClose={() => closeModal('create-plan')} title="ایجاد پلن جدید" size="md">
        <div className="space-y-4 p-2"><Input label="نام پلن" /><Input label="قیمت (ریال)" type="number" /><Select label="دوره" options={[{value:'monthly',label:'ماهانه'},{value:'yearly',label:'سالانه'}]} /><div className="flex justify-end gap-2"><Button variant="outline">انصراف</Button><Button>ایجاد</Button></div></div>
      </Modal>
      <Modal isOpen={modals['register-device']} onClose={() => closeModal('register-device')} title="ثبت دستگاه جدید" size="md">
        <div className="space-y-4 p-2"><Input label="نام دستگاه" /><Select label="نوع" options={[{value:'pos',label:'پاس'},{value:'scanner',label:'اسکنر'},{value:'printer',label:'پرینتر'}]} /><Input label="شناسه دستگاه" /><div className="flex justify-end gap-2"><Button variant="outline">انصراف</Button><Button>ثبت</Button></div></div>
      </Modal>
    </div>
  )
}

function UserForm({ onSubmit }: { onSubmit: () => void }) {
  return (
    <div className="space-y-4 p-2">
      <div className="grid grid-cols-2 gap-4">
        <Input label="نام" placeholder="نام" />
        <Input label="نام خانوادگی" placeholder="نام خانوادگی" />
      </div>
      <Input label="ایمیل" type="email" placeholder="user@company.com" />
      <Input label="رمز عبور" type="password" placeholder="حداقل ۸ کاراکتر" />
      <Select label="نقش" options={[{value:'',label:'انتخاب کنید'},{value:'admin',label:'مدیر سیستم'},{value:'manager',label:'مدیر انبار'},{value:'seller',label:'فروشنده'}]} />
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onSubmit}>انصراف</Button>
        <Button onClick={onSubmit}>ایجاد کاربر</Button>
      </div>
    </div>
  )
}